/**
 * @zendir/ui - PropulsionCard Component
 *
 * Delta-V Budget and Fuel Status visualization.
 * Shows propellant levels and remaining delta-V capability.
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - Tank level visualization
 * - Maneuver feasibility checks
 * - Loading and empty states
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../theme";
import { AstroIcon, type AstroIconName } from "../core/display/AstroIcon";
import {
  safeNumber,
  formatPercentage,
  classNames,
  type StatusLevel,
} from "../utils";

export interface PropulsionTankData {
  id: string;
  name: string;
  /** Current mass in kg */
  currentMassKg: number;
  /** Maximum capacity in kg */
  maxMassKg: number;
  /** Propellant type */
  type: "hydrazine" | "xenon" | "cold_gas" | "bipropellant" | string;
}

export interface ManeuverFeasibility {
  name: string;
  requiredDeltaVMs: number;
  feasible: boolean;
}

export interface PropulsionCardProps {
  /** Propellant tanks data */
  tanks?: PropulsionTankData[];
  /** Remaining delta-V in m/s */
  remainingDeltaVMs?: number;
  /** Total delta-V at full tanks in m/s */
  totalDeltaVMs?: number;
  /** Specific impulse in seconds */
  ispSeconds?: number;
  /** Common maneuvers with feasibility */
  maneuvers?: ManeuverFeasibility[];
  /** Compact mode */
  compact?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Get status level based on percentage
 */
function getPercentageStatus(pct: number | undefined): StatusLevel {
  if (pct === undefined || !Number.isFinite(pct)) return "off";
  if (pct > 50) return "normal";
  if (pct > 20) return "caution";
  if (pct > 5) return "serious";
  return "critical";
}

export const PropulsionCard = memo(function PropulsionCard({
  tanks,
  remainingDeltaVMs,
  totalDeltaVMs,
  ispSeconds,
  maneuvers = [],
  compact = false,
  loading = false,
  className = "",
}: PropulsionCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};

  const transitionDuration = prefersReducedMotion ? "0ms" : "300ms";
  // Medium-soft status colors (readable, not too pale)
  const statusColors: Record<StatusLevel, string> = {
    normal: "#6ed86e",
    caution: "#ffd54f",
    serious: "#ffa726",
    critical: "#e57373",
    off: "#90a4ae",
    standby: "#4fc3f7",
  };

  // Calculate overall propellant percentage
  const _overallPercentage = useMemo(() => {
    if (!tanks || tanks.length === 0) return 0;
    const totalCurrent = tanks.reduce(
      (sum, t) => sum + (t.currentMassKg ?? 0),
      0,
    );
    const totalMax = tanks.reduce((sum, t) => sum + (t.maxMassKg ?? 0), 0);
    return totalMax > 0 ? (totalCurrent / totalMax) * 100 : 0;
  }, [tanks]);

  // Delta-V percentage
  const deltaVPercentage = useMemo(() => {
    const total = totalDeltaVMs ?? 0;
    const remaining = remainingDeltaVMs ?? 0;
    return total > 0 ? (remaining / total) * 100 : 0;
  }, [remainingDeltaVMs, totalDeltaVMs]);

  const deltaVStatus = getPercentageStatus(deltaVPercentage);
  const deltaVColor = statusColors[deltaVStatus] || statusColors.off;

  // Get color based on percentage
  const getPercentageColor = (pct: number | undefined): string => {
    const status = getPercentageStatus(pct);
    return (
      statusColors[status as keyof typeof statusColors] || statusColors.off
    );
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-propulsion-card", "loading", className)}
        role="article"
        aria-busy="true"
        aria-label="Loading propulsion data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 180,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              height: 24,
              width: "40%",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 24,
              width: 80,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
        </div>
        <div
          style={{
            height: 12,
            backgroundColor: "rgba(255,255,255,0.06)",
            borderRadius: 4,
            marginBottom: 16,
          }}
        />
        {[1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 32,
              backgroundColor: "rgba(255,255,255,0.06)",
              borderRadius: 4,
              marginBottom: 8,
            }}
          />
        ))}
      </div>
    );
  }

  // Empty state
  if (!tanks || tanks.length === 0) {
    return (
      <div
        className={classNames("zendir-propulsion-card", "empty", className)}
        role="article"
        aria-label="No propulsion data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <AstroIcon
          name="launch"
          size="normal"
          label=""
          style={{ opacity: 0.5 }}
        />
        <p
          style={{
            margin: "8px 0 0 0",
            fontSize: tokens.typography.body[2].fontSize,
          }}
        >
          No propulsion data available
        </p>
      </div>
    );
  }

  // Tank type icons (AstroIcon)
  const getTankIconName = (type: string): AstroIconName => {
    switch (type) {
      case "hydrazine":
        return "propulsion-power";
      case "xenon":
        return "cloud";
      case "cold_gas":
        return "ac-unit";
      case "bipropellant":
        return "whatshot";
      default:
        return "propulsion-power";
    }
  };

  // Astro UX compliant card styling (transparent theme: glass background)
  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    ...(isTransparentTheme && {
      backdropFilter: "blur(12px)" as const,
      WebkitBackdropFilter: "blur(12px)" as const,
    }),
    border: "none",
    borderRadius: tokens.borderRadius.lg,
    padding: tokens.spacing.md,
    fontFamily: tokens.typography.fontFamily.primary,
    color: tokens.colors.text.primary,
  };

  // Sentence-case label style (Astro guideline)
  const labelStyle: React.CSSProperties = {
    color: tokens.colors.text.tertiary,
    fontSize: tokens.typography.fontSize.xs,
    fontWeight: tokens.typography.fontWeight.medium,
  };

  return (
    <article
      className={classNames("zendir-propulsion-card", className)}
      aria-label="Propulsion system status"
      style={cardStyle}
    >
      {!compact && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: tokens.spacing.md,
          }}
        >
          <div>
            {/* Astro: Sentence-case labels */}
            <h3
              style={{
                fontWeight: tokens.typography.fontWeight.medium,
                fontSize: tokens.typography.fontSize.md,
                color: tokens.colors.text.primary,
                margin: 0,
              }}
            >
              Propulsion
            </h3>
            <p
              style={{
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
                margin: 0,
                marginTop: tokens.spacing.xs,
              }}
            >
              {ispSeconds ? `Isp: ${ispSeconds}s` : "Delta-V budget"}
            </p>
          </div>
          {/* Astro: Tabular numbers for data */}
          <span
            style={{
              fontSize: tokens.typography.fontSize.lg,
              fontWeight: tokens.typography.fontWeight.bold,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
              color: deltaVColor,
            }}
          >
            {safeNumber(remainingDeltaVMs, 0)} m/s
          </span>
        </div>
      )}

      {/* Delta-V gauge */}
      <div style={{ marginBottom: tokens.spacing.md }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: tokens.typography.fontSize.xs,
            marginBottom: tokens.spacing.xs,
          }}
        >
          <span style={labelStyle}>Remaining ΔV</span>
          <span
            style={{
              fontFamily: tokens.typography.fontFamily.mono,
              color: deltaVColor,
            }}
          >
            {formatPercentage(deltaVPercentage, false, 0)}
          </span>
        </div>
        <div
          style={{
            height: "12px",
            backgroundColor: tokens.colors.border.muted,
            borderRadius: tokens.borderRadius.sm,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, Math.max(0, deltaVPercentage))}%`,
              backgroundColor: deltaVColor,
              transition: `width ${transitionDuration} ease`,
              borderRadius: tokens.borderRadius.sm,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
            color: tokens.colors.text.tertiary,
            marginTop: tokens.spacing.xs,
          }}
        >
          <span>0</span>
          <span>{safeNumber(totalDeltaVMs, 0)} m/s</span>
        </div>
      </div>

      {/* Tank levels */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.sm,
          marginBottom: tokens.spacing.md,
        }}
      >
        {tanks.map((tank) => {
          const current = tank.currentMassKg ?? 0;
          const max = tank.maxMassKg ?? 1;
          const pct = max > 0 ? (current / max) * 100 : 0;
          return (
            <div
              key={tank.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.spacing.sm,
              }}
            >
              <AstroIcon
                name={getTankIconName(tank.type)}
                size="small"
                label={tank.type}
                style={{ fontSize: tokens.typography.fontSize.sm }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: tokens.typography.fontSize.xs, // 0.75rem / 12px (AstroUXDS body3)
                    marginBottom: "2px",
                  }}
                >
                  <span style={{ color: tokens.colors.text.secondary }}>
                    {tank.name}
                  </span>
                  <span
                    style={{
                      fontFamily: tokens.typography.fontFamily.mono,
                      color: tokens.colors.text.tertiary,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {safeNumber(current, 1)} / {safeNumber(max, 1)} kg
                  </span>
                </div>
                <div
                  style={{
                    height: "6px",
                    backgroundColor: tokens.colors.border.muted,
                    borderRadius: tokens.borderRadius.sm,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${pct}%`,
                      backgroundColor: getPercentageColor(pct),
                      borderRadius: tokens.borderRadius.sm,
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Maneuver feasibility - Astro: Sentence-case */}
      {maneuvers.length > 0 && !compact && (
        <div
          style={{
            paddingTop: tokens.spacing.md,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
          }}
        >
          <div
            style={{
              ...labelStyle,
              marginBottom: tokens.spacing.sm,
            }}
          >
            Maneuver feasibility
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: tokens.spacing.xs,
            }}
          >
            {maneuvers.map((m, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: tokens.typography.fontSize.xs,
                }}
              >
                <span style={{ color: tokens.colors.text.secondary }}>
                  {m.name}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: tokens.spacing.sm,
                  }}
                >
                  <span
                    style={{
                      fontFamily: tokens.typography.fontFamily.mono,
                      color: tokens.colors.text.tertiary,
                    }}
                  >
                    {m.requiredDeltaVMs} m/s
                  </span>
                  <span
                    style={{
                      fontWeight: tokens.typography.fontWeight.bold,
                      color: m.feasible
                        ? tokens.colors.status.normal
                        : tokens.colors.status.critical,
                    }}
                  >
                    {m.feasible ? "✓" : "✗"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warning if low - Astro notification style */}
      {deltaVPercentage < 20 && (
        <div
          style={{
            marginTop: tokens.spacing.md,
            padding: tokens.spacing.sm,
            backgroundColor: `${tokens.colors.status.critical}15`,
            border: `1px solid ${tokens.colors.status.critical}40`,
            borderRadius: tokens.borderRadius.md,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.status.critical,
          }}
        >
          ⚠️ Low propellant - limit maneuvers
        </div>
      )}
    </article>
  );
});

export default PropulsionCard;
